/**
 * Auth API
 *
 * Endpoints:
 *   POST api/v1/auth/signin      - signIn
 *   POST api/v1/auth/signup       - signUp, signInWithFacebook
 *   POST api/v1/users/reset-password - resetPassword
 *   POST api/v1/users/reset-password/check   - checkResetToken
 *   POST api/v1/users/reset-password/confirm - confirmPasswordReset
 *   POST api/v1/auth/:type        - signInWithFamilyTreeProvider
 *   POST api/v1/auth/check-email  - checkEmail
 *   POST api/v1/auth/instagram    - addInstagramProvider
 */
import SETTINGS from '../settings';
import { apiClientPostRequest } from '../lib/backendRequest';

const authApi = countryCode => ({
  /**
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{data: object, error}>}
   */
  async signIn(email, password) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/signin',
        {
          auth: {
            email,
            password,
            provider: 0,
          },
          country_alpha2: countryCode,
        },
        {},
        {
          logBodyDuringIssue: false,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return {
        error: {
          code: e.code || 500,
          body: e.body || undefined,
          isNetworkError: Boolean(e.isNetworkError || e.isAborted),
        },
      };
    }
  },

  /**
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{data, error}>}
   */
  async signUp(email, password) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/signup',
        {
          auth: {
            email,
            password,
            provider: 0,
          },
          country_alpha2: countryCode,
        },
        {},
        {
          logBodyDuringIssue: false,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return {
        error: {
          code: e.code || 500,
          body: e.body || undefined,
          // 'email_taken' when the email already has a password account.
          reason: e.reason,
          isNetworkError: Boolean(e.isNetworkError || e.isAborted),
        },
      };
    }
  },

  /**
   * @param {string} email
   * @returns {Promise<{data: boolean, error}>}
   */
  async resetPassword(email) {
    try {
      // 204 with an empty body. Safari rejects response.json() on it with a DOMException
      // that has code 12, which read as a failed send.
      await apiClientPostRequest('api/v1/users/reset-password', {
        email,
        redirect_uri: SETTINGS.HOST,
      });

      return { data: true };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Checks the token from the reset email without using it up.
   *
   * @param {string} token
   * @returns {Promise<{data: {email: string}, error: number}>} error 404 for an unknown or expired link
   */
  async checkResetToken(token) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/reset-password/check',
        { token },
        {},
        { logBodyDuringIssue: false },
      );
      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code || 500 };
    }
  },

  /**
   * Sets a new password and signs the user in.
   *
   * @param {string} token
   * @param {string} password
   * @returns {Promise<{data: object, error: number}>} data has the access token, same as signIn
   */
  async confirmPasswordReset(token, password) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/reset-password/confirm',
        { token, password },
        {},
        { logBodyDuringIssue: false },
      );
      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code || 500 };
    }
  },

  /**
   * @param {string} userId
   * @param {string} name
   * @param {string} email
   * @returns {Promise<{data: object, error}>}
   */
  async signInWithFacebook(userId, name, email) {
    try {
      const response = await apiClientPostRequest('api/v1/auth/signup', {
        auth: {
          user_id: userId,
          provider: 5,
          name,
          email,
        },
        country_alpha2: countryCode,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * When infoONly is true, data only returns object provider. Don't use for sign in. The main purpose is to add Ancestry provider to the existing account.
   * Returns { provider }
   *
   * When infoOnly is false, it's classic signIn api call.
   * Returns { userData }
   *
   * @param {string} authorizationCode
   * @param {boolean} infoOnly
   */
  async signInWithFamilyTreeProvider(type, authorizationCode, infoOnly = false) {
    try {
      const response = await apiClientPostRequest(`api/v1/auth/${type}`, {
        code: authorizationCode,
        info_only: infoOnly,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Whether the email has a password account.
   *
   * @param {string} email
   * @returns {Promise<{data: {exists: boolean}, error: number}>}
   */
  async checkEmail(email) {
    try {
      // 204 has no body, so the response is not parsed.
      await apiClientPostRequest('api/v1/auth/check-email', { email });

      return { data: { exists: true } };
    } catch (e) {
      if (e.code === 404) return { data: { exists: false } };
      return { error: e.code || 500 };
    }
  },

  /**
   * Returns { }
   *
   * @param {string} token original user`s access_token
   * @param {string} code instagram code
   * @param {string} redirectUrl
   */
  async addInstagramProvider(token, code, redirectUrl) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/instagram',
        {
          code,
          redirect_url: redirectUrl,
          country_alpha2: countryCode,
        },
        {
          Authorization: `Token ${token}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },
});

export default authApi;
